import React, { useState, useEffect, Suspense, lazy, useMemo } from 'react';

import { ThemeProvider } from '@mui/material/styles';
import { MRT_RowData, MRT_ColumnDef } from 'material-react-table';
import CircularProgress from '@mui/material/CircularProgress';
const MaterialReactTable = lazy(() =>
  import('material-react-table').then((module) => ({
    default: module.MaterialReactTable,
  })),
);
const PopupDialog = lazy(() => import('./PopupDialog'));

import Papa from 'papaparse';
import Linkify from 'linkify-react';

import { Results } from '../types/types';
import { getTableOptions } from '../utils/tableOptions';
import { linkifyOptions } from '../utils/linkifyOptions';
import { getLocalizedLanguagePack } from './../utils/language';
import { setFilterVariants, filterIncludedColumns, filterInitialVisibleColumns } from '../utils/columnFiltering';
import { getTheme } from './../utils/theme';

import './../styles/styleFixes.css';
import './../styles/styleChanges.css';

export default function Table() {
  const [data, setData] = useState<Record<string, any>[]>([]);
  const [columns, setColumns] = useState<MRT_ColumnDef<any>[]>([]);
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({});

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [dialogRowData, setDialogRowData] = useState<MRT_RowData | null>(null);

  const { requiredOptions, optionalOptions } = useMemo(() => getTableOptions(), []);

  const theme = useMemo(() => getTheme(), []);
  const localization = useMemo(() => getLocalizedLanguagePack(), []);

  function configureResults(results) {
    // Put on own predictable form
    const originalResults: Results = {
      data: results.data,
      fields: results.meta.fields.map((name) => ({
        name: name,
        filterVariant: optionalOptions._defaultFilterVariant,
      })),
    };

    // Conform results
    let filteredResults = setFilterVariants(originalResults, optionalOptions._overrideFilterVariants);
    filteredResults = filterIncludedColumns(filteredResults, optionalOptions._columnsToFetch);

    // Set initial visible columns
    setVisibleColumns(filterInitialVisibleColumns(originalResults, optionalOptions._initialVisibleColumns));

    // Configure each column for MRT
    const generatedColumns = filteredResults.fields.map((field) => {
      return {
        header: field.name,
        accessorKey: field.name,
        filterVariant: field.filterVariant,
        Cell: ({ cell }) => <Linkify options={linkifyOptions}>{cell.getValue()}</Linkify>,
      };
    });

    // Set info for Material-React-Table
    setColumns(generatedColumns);
    setData(filteredResults.data);
  }

  useEffect(() => {
    async function prepareTable() {
      const response = await fetch(requiredOptions._tableUrl);
      const csvText = await response.text();

      Papa.parse(csvText, {
        header: true,
        skipEmptyLines: 'greedy',
        complete: (results) => {
          configureResults(results);
        },
      });
    }
    prepareTable();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <Suspense fallback={<CircularProgress />}>
        {Object.keys(visibleColumns).length === 0 ? (
          <CircularProgress />
        ) : (
          <MaterialReactTable
            columns={columns}
            data={data}
            initialState={{
              pagination: { pageIndex: 0, pageSize: optionalOptions._initialRowsPerPage },
              density: optionalOptions._initialDensity,
              showColumnFilters: optionalOptions._enableFiltering,
              columnVisibility: visibleColumns,
            }}
            muiPaginationProps={{
              rowsPerPageOptions: optionalOptions._rowsPerPageOptions,
            }}
            enableFacetedValues={true}
            enableColumnFilters={optionalOptions._enableFiltering}
            enablePagination={optionalOptions._enablePagination}
            enableStickyHeader={optionalOptions._enableStickyHeader}
            enableSorting={optionalOptions._enableSorting}
            enableDensityToggle={optionalOptions._enableDensityToggle}
            enableFullScreenToggle={optionalOptions._enableFullScreenToggle}
            enableHiding={optionalOptions._enableColumnVisibilityToggle}
            enableGlobalFilter={optionalOptions._enableSearch}
            localization={localization}
            muiTableContainerProps={{
              sx: optionalOptions._maxTableHeight ? { maxHeight: optionalOptions._maxTableHeight } : {},
            }}
            muiTableBodyRowProps={({ row }) => ({
              onClick: optionalOptions._enableModalWindow
                ? () => {
                    setDialogRowData(row.original);
                    setDialogOpen(true);
                  }
                : undefined,
              sx: optionalOptions._enableModalWindow ? { cursor: 'pointer' } : {},
            })}
          />
        )}

        {optionalOptions._enableModalWindow && (
          <PopupDialog dialogOpen={dialogOpen} setDialogOpen={setDialogOpen} dialogRowData={dialogRowData} />
        )}
      </Suspense>
    </ThemeProvider>
  );
}
